/**
 * Generic, security-conscious iframe embed handler.
 * Used by sections/iframe-embed.liquid and the hero search embed in sections/hero-split.liquid.
 * - Only ever sets an iframe `src` if the URL's hostname matches an allow-listed domain.
 * - Only trusts postMessage auto-resize events whose origin matches the same allow-list.
 */
(function () {
  function isAllowedHost(hostname, allowedDomains) {
    hostname = (hostname || '').toLowerCase();
    return allowedDomains.some(function (domain) {
      return hostname === domain || hostname.endsWith('.' + domain);
    });
  }

  function isAllowedUrl(url, allowedDomains) {
    try {
      var u = new URL(url, window.location.origin);
      return u.protocol === 'https:' && isAllowedHost(u.hostname, allowedDomains);
    } catch (e) {
      return false;
    }
  }

  function isAllowedOrigin(origin, allowedDomains) {
    try {
      var u = new URL(origin);
      return isAllowedHost(u.hostname, allowedDomains);
    } catch (e) {
      return false;
    }
  }

  function initWrap(wrap) {
    if (wrap.dataset.iframeEmbedInit) return;
    wrap.dataset.iframeEmbedInit = 'true';

    var iframe = wrap.querySelector('.iframe-embed__iframe');
    if (!iframe) return;

    var placeholder = wrap.querySelector('.iframe-embed__placeholder');
    var errorBox = wrap.querySelector('.iframe-embed__error');
    var spinner = wrap.querySelector('.iframe-embed__spinner');
    var fallback = wrap.parentElement ? wrap.parentElement.querySelector('[data-iframe-embed-fallback]') : null;
    var fallbackLink = fallback ? fallback.querySelector('[data-iframe-embed-fallback-link]') : null;

    var allowedDomains = (wrap.dataset.allowed || '')
      .split(',')
      .map(function (d) { return d.trim().toLowerCase(); })
      .filter(Boolean);

    function showState(state) {
      if (spinner) spinner.hidden = state !== 'loading';
      if (placeholder) placeholder.hidden = state !== 'placeholder';
      if (errorBox) errorBox.hidden = state !== 'error';
      iframe.hidden = state !== 'ready';
      // Only reserve the full (potentially huge, e.g. 2200-3200px) configured content
      // height once the iframe is actually showing content. See the matching CSS rule
      // for why: without this, the small status message/spinner ends up centered deep
      // inside an almost entirely blank column.
      wrap.classList.toggle('is-ready', state === 'ready');
    }

    var src = wrap.dataset.mode === 'query_param'
      ? new URLSearchParams(window.location.search).get(wrap.dataset.param || 'src')
      : wrap.dataset.src;

    if (!src) {
      showState('placeholder');
      return;
    }

    if (!isAllowedUrl(src, allowedDomains)) {
      showState('error');
      return;
    }

    if (fallback && fallbackLink) {
      fallbackLink.href = src;
      fallback.hidden = false;
    }

    if (wrap.dataset.autoResize === 'true') {
      window.addEventListener('message', function (event) {
        if (event.source !== iframe.contentWindow) return;
        if (!isAllowedOrigin(event.origin, allowedDomains)) return;
        if (!event.data || event.data.type !== 'orivet:resize') return;
        var height = Number(event.data.height);
        if (!Number.isFinite(height) || height <= 0) return;
        height = Math.min(Math.max(height, 200), 4000);
        iframe.style.height = height + 'px';
      });
    }

    // Some embedded pages (e.g. the hero search box) render results as normal in-page
    // content below the input, growing the *embedded* document's height as the visitor
    // types — but never call postMessage to tell us about it (confirmed by inspecting
    // their source). Since the iframe is cross-origin, we can't read its real content
    // height directly to auto-resize precisely. As a practical stand-in, grow the iframe
    // to a generous fixed height while the visitor is actually focused inside it (so a
    // dropdown of results has room to render), and shrink it back once focus leaves.
    // This relies only on cross-origin-safe window-level focus/blur events — never on
    // reading anything inside the iframe's document.
    if (wrap.dataset.expandOnFocus === 'true') {
      var expandedHeight = Number(wrap.dataset.expandedHeight) || 520;
      // Captured lazily (on first expand) rather than eagerly at init time, since at
      // init the iframe may not have its final layout height yet (e.g. before
      // `beginLoad()`/the IntersectionObserver reveal it) — reading it too early could
      // capture "0px" and break the collapse-back behavior later.
      var collapsedHeight = null;

      window.addEventListener('blur', function () {
        window.setTimeout(function () {
          if (document.activeElement === iframe) {
            if (collapsedHeight === null) {
              collapsedHeight = iframe.style.height || getComputedStyle(iframe).height;
            }
            wrap.classList.add('is-expanded');
            iframe.style.height = expandedHeight + 'px';
          }
        }, 0);
      });

      window.addEventListener('focus', function () {
        if (document.activeElement !== iframe && collapsedHeight !== null) {
          wrap.classList.remove('is-expanded');
          iframe.style.height = collapsedHeight;
        }
      });
    }

    // Loading the actual iframe (and starting the timers below) is deferred until the embed
    // scrolls into view. Otherwise, for a section far down the page, the browser's native
    // `loading="lazy"` on the <iframe> can delay the real network fetch well past our timeout,
    // causing a false "error" state before the visitor ever sees it.
    //
    // We deliberately do NOT reveal the iframe only on its `load` event: that event only fires
    // once EVERY resource in the embedded document has finished loading (all images included),
    // and on the breed-library page specifically it was confirmed (via manually removing the
    // `hidden` attribute in devtools) that the visible content renders correctly long before
    // `load` fires — sometimes `load` never fires at all within any reasonable time (e.g. a
    // slow-loading/failing analytics or font request that never resolves keeps the document
    // "loading" forever from the browser's point of view, even though it looks complete).
    // So instead we optimistically reveal the iframe after a short delay regardless of `load`,
    // while still reacting immediately (and earlier) to a real `load` or `error` event if either
    // fires first. Only if NOTHING happens at all within the full 45s do we show a real error.
    function beginLoad() {
      showState('loading');
      var settled = false;

      var hardTimeout = window.setTimeout(function () {
        if (settled) return;
        settled = true;
        showState('error');
      }, 45000);

      var optimisticTimeout = window.setTimeout(function () {
        if (settled) return;
        settled = true;
        window.clearTimeout(hardTimeout);
        showState('ready');
      }, 6000);

      iframe.addEventListener('load', function () {
        if (settled) return;
        settled = true;
        window.clearTimeout(hardTimeout);
        window.clearTimeout(optimisticTimeout);
        showState('ready');
      }, { once: true });

      iframe.addEventListener('error', function () {
        if (settled) return;
        settled = true;
        window.clearTimeout(hardTimeout);
        window.clearTimeout(optimisticTimeout);
        showState('error');
      }, { once: true });

      iframe.src = src;
    }

    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            observer.disconnect();
            beginLoad();
          }
        });
      }, { rootMargin: '200px 0px' });
      observer.observe(wrap);
    } else {
      beginLoad();
    }
  }

  function initAll() {
    document.querySelectorAll('[data-iframe-embed]').forEach(initWrap);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
})();
